import _upperFirst from 'lodash/upperFirst';

/**
 * @param { error: boolean } [renderState]
 * @param {import('utils/types').SelectStyleOverrides} [overrides]
 */
export function getCustomSelectStyles({ error = false } = {}, overrides = {}) {
  return {
    container: override(containerStyles, overrides.container),
    control: override(controlStyles, overrides.control, { error }),
    valueContainer: override(valueContainerStyles, overrides.valueContainer),
    singleValue: override(singleValueStyles, override.singleValue),
    input: override(inputStyles, overrides.input),
    dropdownIndicator: override(
      dropdownIndicatorStyles,
      overrides.dropdownIndicator
    ),
    group: override(groupStyles, overrides.group),
    groupHeading: override(groupHeadingStyles, overrides.groupHeading),
    indicatorsContainer: override(
      indicatorsContainerStyles,
      overrides.indicatorsContainer
    ),
    clearIndicator: override(clearIndicatorStyles, overrides.clearIndicator),
    indicatorSeparator: override(
      indicatorSeparatorStyles,
      overrides.indicatorSeparator
    ),
    placeholder: override(placeholderStyles, overrides.placeholder),
    menu: override(menuStyles, overrides.menu),
    menuList: override(menuListStyles, overrides.menuList),
    option: override(optionStyles, overrides.option),
    multiValue: override(multiValueStyles, overrides.multiValue),
    multiValueLabel: override(multiValueLabelStyles, overrides.multiValueLabel),
    multiValueRemove: override(
      multiValueRemoveStyles,
      overrides.multiValueRemove
    ),
    noOptionsMessage: override(
      noOptionsMessageStyles,
      overrides.noOptionsMessage
    )
  };
}

export function formatGroupLabel(data) {
  return _upperFirst(data.label);
}

// Style Helpers 👇 ----------------------------------------------------------

const override = (
  stylesFactory = () => {},
  overrideFactory = () => {},
  customArgs = {}
) => {
  return (...reactSelectArgs) => ({
    ...stylesFactory(reactSelectArgs, customArgs),
    ...overrideFactory(reactSelectArgs, customArgs)
  });
};

const containerStyles = ([provided]) => {
  return {
    ...provided,
    fontSize: '14px',
    borderRadius: '4px',
    pointerEvents: 'auto',
    position: 'relative'
  };
};

const controlStyles = (
  [
    provided,
    {
      isDisabled,
      isFocused,
      menuIsOpen,
      selectProps: { menuPosition }
    }
  ],
  { error = false } = {}
) => {
  const isRelative = menuPosition === 'relative';
  return {
    ...provided,
    zIndex: '1',
    gap: '8px',
    minHeight: '34px',
    padding: '4px 8px',
    borderRadius: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor:
      error && !isDisabled
        ? '#E32707'
        : isDisabled
        ? 'var(--gray-300)'
        : isFocused || (menuIsOpen && !isRelative)
        ? '#006FBB'
        : 'var(--gray-500)',
    backgroundColor:
      error && !isDisabled
        ? '#ffffff'
        : isDisabled
        ? 'var(--gray-100)'
        : menuIsOpen && !isRelative
        ? '#EBF5FA'
        : '#ffffff',
    cursor: isDisabled ? 'not-allowed' : 'unset',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: error || isDisabled ? 'none' : '0px 0px 0px 1px #B4E0FA',
      borderColor:
        error && !isDisabled
          ? '#E32707'
          : isDisabled
          ? 'var(--gray-300)'
          : '#006FBB'
    }
  };
};

const valueContainerStyles = ([provided]) => {
  return {
    ...provided,
    padding: '0',
    gap: '4px'
  };
};

const singleValueStyles = ([provided, { isDisabled }]) => {
  return {
    ...provided,
    margin: '0',
    fontWeight: '400',
    color: isDisabled ? 'var(--gray-700)' : '#212C36'
  };
};

const inputStyles = ([provided]) => {
  return {
    ...provided,
    margin: '0',
    padding: '0',
    color: '#212C36'
  };
};

const placeholderStyles = ([provided]) => {
  return {
    ...provided,
    color: 'var(--gray-500)',
    fontWeight: 400
  };
};

const indicatorsContainerStyles = ([provided]) => {
  return {
    ...provided,
    gap: '8px'
  };
};

const clearIndicatorStyles = ([provided]) => {
  return { ...provided };
};

const indicatorSeparatorStyles = () => {
  return {
    display: 'none'
  };
};

const dropdownIndicatorStyles = ([
  provided,
  {
    selectProps: { menuPosition }
  }
]) => {
  const isRelative = menuPosition === 'relative';
  return {
    ...provided,
    padding: '0',
    transition: 'all 0.15s',
    display: isRelative ? 'none' : 'flex'
  };
};

const menuStyles = ([
  provided,
  {
    selectProps: { menuPosition }
  }
]) => {
  const isRelative = menuPosition === 'relative';
  return {
    ...provided,
    width: 'unset',
    minWidth: '100%',
    position: menuPosition,
    top: '100%',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    border: '1px solid var(--gray-300)',
    boxShadow: !isRelative ? '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' : 'none',
    zIndex: isRelative ? 'unset' : '100000',
    overflow: 'hidden',
    margin: '4px 0 0 0'
  };
};

const menuListStyles = ([
  provided,
  {
    selectProps: { menuPosition }
  }
]) => {
  const isRelative = menuPosition === 'relative';
  return {
    ...provided,
    padding: '0',
    position: 'relative',
    height: isRelative ? '300px' : 'unset'
  };
};

const groupStyles = ([provided]) => {
  return {
    ...provided,
    padding: '0',
    borderBottom: '1px solid var(--gray-300)',
    '&:last-child': {
      borderBottom: 'none'
    }
  };
};

const groupHeadingStyles = ([provided]) => {
  return {
    ...provided,
    margin: 0,
    fontSize: '14px',
    fontWeight: '500',
    minHeight: '37px',
    paddingTop: '6px',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingBottom: '6px',
    color: '#212C36',
    backgroundColor: 'var(--gray-100)',
    position: 'sticky',
    top: '0',
    textTransform: 'unset',
    zIndex: '1',
    borderBottom: '1px solid var(--gray-300)',
    '&:last-child': {
      borderBottom: 'none'
    }
  };
};

const optionStyles = ([provided, props]) => {
  const {
    data,
    isMulti,
    // value,
    isFocused,
    isSelected,
    isDisabled,
    selectProps: { isNested }
  } = props;

  const depth = isNested ? data?.depth || 0 : 0;
  // const isSelectAllOption = value === SELECT_ALL_OPTION_ID;

  return {
    ...provided,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    padding: '6px 8px',
    minHeight: '37px',
    paddingLeft: `${8 + depth * 24}px`,
    opacity: isDisabled ? 0.5 : 1,
    fontWeight: '400',
    color: '#212C36',
    backgroundColor:
      isSelected && !isMulti && !isNested
        ? '#EBF5FA'
        : isFocused
        ? 'var(--gray-50)'
        : 'transparent',
    // backgroundColor:
    //   isSelected && !isMulti && !isNested
    //     ? '#EBF5FA'
    //     : isFocused
    //     ? 'var(--gray-50)'
    //     : isSelectAllOption
    //     ? '#ffffff'
    //     : 'transparent',
    // position: isSelectAllOption && !isDisabled ? 'sticky' : 'static',
    // top: 0,
    // zIndex: isSelectAllOption ? 1 : 'auto',
    borderBottom: '1px solid var(--gray-300)',
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:active': {
      backgroundColor:
        isSelected && !isMulti && !isNested
          ? '#EBF5FA'
          : isFocused
          ? 'var(--gray-50)'
          : 'transparent'
      // backgroundColor:
      //   isSelected && !isMulti && !isNested
      //     ? '#EBF5FA'
      //     : isFocused
      //     ? 'var(--gray-50)'
      //     : isSelectAllOption
      //     ? '#ffffff'
      //     : 'transparent'
    }
  };
};

const multiValueStyles = ([provided, { isDisabled }]) => {
  return {
    ...provided,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '0',
    margin: '0',
    borderRadius: '0',
    backgroundColor: 'transparent'
  };
};

const multiValueLabelStyles = ([provided, { isDisabled }]) => {
  return {
    ...provided,
    padding: '0',
    paddingLeft: '0',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '16px',
    color: '#FFFFFF'
  };
};

const multiValueRemoveStyles = ([provided, { isDisabled }]) => {
  return {
    ...provided,
    padding: '0',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  };
};

const noOptionsMessageStyles = ([provided]) => {
  return {
    ...provided,
    textAlign: 'left',
    padding: '6px 8px'
  };
};
